import { isInPast, startOfDateInLocalTimezone } from '@seen/core/dates'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'

export function ConfirmWithAnnotationDialog(): JSX.Element {
  const isOpen = useConfirmWithAnnotationStore((state) => state.isOpen)
  const title = useConfirmWithAnnotationStore((state) => state.title)
  const dateLabel = useConfirmWithAnnotationStore((state) => state.dateLabel)
  const description = useConfirmWithAnnotationStore(
    (state) => state.description
  )
  const onConfirm = useConfirmWithAnnotationStore((state) => state.onConfirm)
  const note = useConfirmWithAnnotationStore((state) => state.note)
  const setIsOpen = useConfirmWithAnnotationStore((state) => state.setIsOpen)
  const setNote = useConfirmWithAnnotationStore((state) => state.setNote)
  const isRichText = useConfirmWithAnnotationStore((state) => state.isRichText)
  const setIsRichText = useConfirmWithAnnotationStore(
    (state) => state.setIsRichText
  )
  const includeDate = useConfirmWithAnnotationStore(
    (state) => state.includeDate
  )
  const date = useConfirmWithAnnotationStore((state) => state.date)
  const setDate = useConfirmWithAnnotationStore((state) => state.setDate)
  const noteRequired = useConfirmWithAnnotationStore(
    (state) => state.noteRequired
  )
  const noteLabel = useConfirmWithAnnotationStore((state) => state.noteLabel)
  const pastDateWarning = useConfirmWithAnnotationStore(
    (state) => state.pastDateWarning
  )

  const handleDateChange = (dateString: string | null): void => {
    if (dateString) {
      const newDate = startOfDateInLocalTimezone(dateString)
      setDate(newDate)
    }
  }

  const handleNoteChange = (newNote: string): void => {
    setNote(newNote)
  }

  const canConfirm = note.trim().length > 0 || !noteRequired

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogDisplay>
        <DialogHeader asChild>
          <DialogTitle>
            <p>{title}</p>
          </DialogTitle>
        </DialogHeader>
        <DialogContent className="py-0">
          <DialogDescription>{description}</DialogDescription>
          {includeDate && (
            <div className="mb-4 grid gap-2">
              <label htmlFor="date" className="text-sm font-medium">
                {dateLabel}
              </label>
              <DatePicker
                value={date}
                onChange={handleDateChange}
                className="w-full"
                clearText=""
              />
              {pastDateWarning && isInPast(date) && pastDateWarning}
            </div>
          )}
          <div className="grid gap-2">
            <label htmlFor="note" className="text-sm font-medium">
              {noteLabel}{' '}
              {noteRequired && <span className="text-red-500">*</span>}
            </label>
            {isRichText ? (
              <RichTextEditor
                variant="unstyled"
                value={note}
                onChange={handleNoteChange}
                placeholder="Enter your note here..."
              />
            ) : (
              <Input
                id="note"
                className="mt-1"
                placeholder="Enter your note here..."
                value={note}
                onChange={(e) => handleNoteChange(e.target.value)}
              />
            )}
            {noteRequired && note.trim().length === 0 && (
              <p className="text-sm text-red-500">{noteLabel} is required</p>
            )}
          </div>
        </DialogContent>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onConfirm(note, includeDate ? date : undefined)
              setIsOpen(false)
              setIsRichText(false)
              setNote('')
            }}
            disabled={!canConfirm}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogDisplay>
    </Dialog>
  )
}
